import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './ControlAsistencia.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';
import { colorBg } from '../colorCaravana';
import AyudaModal from './AyudaModal';
import { AYUDA_ASISTENCIA } from '../ayudaContenido';

const BASE = API_CONFIG.BASE_URL + '/api/asistencias';
const ANIMALES = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.ANIMALES;

const TIPO_LABEL = { VACUNACION: 'Vacunación', ANTIAFTOSA: 'Antiaftosa', CONTROL: 'Control', INSEMINACION: 'Inseminación', OTROS: 'Otros' };
const CLASIF_LABEL = {
  VACA: 'Vaca', VAQUILLA: 'Vaquilla', NOVILLO: 'Novillo', TORO: 'Toro',
  DESM_MACHO: 'Desm. Macho', DESM_HEMBRA: 'Desm. Hembra',
  TERNERO_MACHO: 'Ternero Macho', TERNERO_HEMBRA: 'Ternero Hembra',
};

export default function ControlAsistencia({ onVerFicha }) {
  const [vista, setVista] = useState('lista'); // 'lista' | 'conteo' | 'comparacion'
  const [asistencias, setAsistencias] = useState([]);
  const [asistenciaActual, setAsistenciaActual] = useState(null);
  const [comparacion, setComparacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingComp, setLoadingComp] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  // Formulario nueva asistencia
  const [formNueva, setFormNueva] = useState({ fecha: hoy(), tipo: 'CONTROL', observacion: '' });
  const [mostrarFormNueva, setMostrarFormNueva] = useState(false);

  // Búsqueda de caravana
  const [caravanaBuscar, setCaravanaBuscar] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const inputRef = useRef(null);

  // Filtro comparación
  const [filtroComp, setFiltroComp] = useState('todos'); // 'todos' | 'presentes' | 'ausentes'

  // Reporte final
  const [mostrarReporte, setMostrarReporte] = useState(false);
  const [reporteComp, setReporteComp] = useState(null);
  const [filtroReporte, setFiltroReporte] = useState('todos');

  // Vacunación masiva
  const [mostrarVacuna, setMostrarVacuna] = useState(false);
  const [formVacuna, setFormVacuna] = useState({
    nombreVacuna: '', laboratorio: '', lote: '', fechaAplicacion: hoy(),
    fechaProximaAplicacion: '', dosis: '', veterinario: '', observaciones: '',
  });
  const [guardandoVacuna, setGuardandoVacuna] = useState(false);

  // Ayuda
  const [mostrarAyuda, setMostrarAyuda] = useState(false);

  // Inseminación masiva
  const [mostrarInsem, setMostrarInsem] = useState(false);
  const [formInsem, setFormInsem] = useState({ fecha: hoy(), observaciones: '' });
  const [guardandoInsem, setGuardandoInsem] = useState(false);

  function hoy() {
    return new Date().toISOString().split('T')[0];
  }

  const mostrarError = (msg) => { setError(msg); setTimeout(() => setError(''), 4000); };
  const mostrarExito = (msg) => { setExito(msg); setTimeout(() => setExito(''), 3000); };

  const cargarLista = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(BASE);
      setAsistencias(await res.json());
    } catch { mostrarError('Error cargando asistencias.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  const crearAsistencia = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch(BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formNueva),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const nueva = await res.json();
      setMostrarFormNueva(false);
      setFormNueva({ fecha: hoy(), tipo: 'CONTROL', observacion: '' });
      abrirConteo(nueva.id);
    } catch (e) { mostrarError(e.message); }
  };

  const abrirConteo = async (id) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${BASE}/${id}`);
      setAsistenciaActual(await res.json());
      setVista('conteo');
      setComparacion(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch { mostrarError('Error abriendo asistencia.'); }
    finally { setLoading(false); }
  };

  const buscarCaravana = async (e) => {
    e.preventDefault();
    if (!caravanaBuscar.trim()) return;
    setBuscando(true);
    setResultadosBusqueda([]);
    try {
      const res = await apiFetch(`${ANIMALES}/buscar?caravana=${encodeURIComponent(caravanaBuscar.trim())}`);
      if (!res.ok) throw new Error('Error buscando');
      const data = await res.json();
      if (data.length === 0) {
        mostrarError(`No se encontró animal con caravana "${caravanaBuscar.trim()}"`);
      } else if (data.length === 1) {
        // Solo uno: agregar directamente
        await agregarPorId(data[0].id, data[0].caravana);
      } else {
        // Varios: mostrar para elegir
        setResultadosBusqueda(data);
      }
    } catch (e) { mostrarError(e.message); }
    finally { setBuscando(false); }
  };

  const agregarPorId = async (animalId, caravana) => {
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/animales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ animalId }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const updated = await res.json();
      setAsistenciaActual(updated);
      setCaravanaBuscar('');
      setResultadosBusqueda([]);
      mostrarExito(`Caravana ${caravana} (ID:${animalId}) agregada ✓`);
      inputRef.current?.focus();
    } catch (e) { mostrarError(e.message); }
  };

  const quitarAnimal = async (animalId, caravana) => {
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/animales/${animalId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json()).message);
      setAsistenciaActual(await res.json());
      mostrarExito(`Caravana ${caravana} quitada.`);
    } catch (e) { mostrarError(e.message); }
  };

  const cerrarAsistencia = async () => {
    if (!window.confirm('¿Cerrar esta asistencia? Ya no se podrán agregar animales.')) return;
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/cerrar`, { method: 'POST' });
      const cerrada = await res.json();
      setAsistenciaActual(cerrada);
      cargarLista();
      const resC = await apiFetch(`${BASE}/${cerrada.id}/comparacion`);
      setReporteComp(await resC.json());
      setFiltroReporte('todos');
      setMostrarReporte(true);
    } catch (e) { mostrarError(e.message); }
  };

  const reabrirAsistencia = async () => {
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/reabrir`, { method: 'POST' });
      setAsistenciaActual(await res.json());
      mostrarExito('Asistencia reabierta.');
    } catch (e) { mostrarError(e.message); }
  };

  const verComparacion = async () => {
    setLoadingComp(true);
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/comparacion`);
      setComparacion(await res.json());
      setVista('comparacion');
      setFiltroComp('todos');
    } catch { mostrarError('Error cargando comparación.'); }
    finally { setLoadingComp(false); }
  };

  const animalesComparacion = () => {
    if (!comparacion) return [];
    if (filtroComp === 'presentes') return comparacion.presentes;
    if (filtroComp === 'ausentes') return comparacion.ausentes;
    return [...comparacion.presentes, ...comparacion.ausentes]
      .sort((a, b) => a.caravana.localeCompare(b.caravana));
  };

  const verReporte = async (id) => {
    setLoading(true);
    try {
      const [resA, resC] = await Promise.all([
        apiFetch(`${BASE}/${id}`),
        apiFetch(`${BASE}/${id}/comparacion`),
      ]);
      setAsistenciaActual(await resA.json());
      setReporteComp(await resC.json());
      setFiltroReporte('todos');
      setMostrarReporte(true);
    } catch { mostrarError('Error cargando reporte.'); }
    finally { setLoading(false); }
  };

  const vacunarMasivo = async (e) => {
    e.preventDefault();
    if (!formVacuna.nombreVacuna.trim()) return;
    setGuardandoVacuna(true);
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/vacunar-masivo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formVacuna),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const data = await res.json();
      setMostrarVacuna(false);
      setFormVacuna({ nombreVacuna: '', laboratorio: '', lote: '', fechaAplicacion: hoy(),
        fechaProximaAplicacion: '', dosis: '', veterinario: '', observaciones: '' });
      mostrarExito(`✅ Vacuna registrada en ${data.total} animales.`);
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoVacuna(false); }
  };

  const inseminarMasivo = async (e) => {
    e.preventDefault();
    if (!formInsem.fecha) return;
    setGuardandoInsem(true);
    try {
      const res = await apiFetch(`${BASE}/${asistenciaActual.id}/inseminar-masivo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formInsem),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const data = await res.json();
      setMostrarInsem(false);
      setFormInsem({ fecha: hoy(), observaciones: '' });
      mostrarExito(`✅ Inseminación registrada en ${data.total} animales.`);
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoInsem(false); }
  };

  const exportarPDF = () => {
    if (!asistenciaActual || !reporteComp) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const fecha = new Date(asistenciaActual.fecha + 'T00:00:00').toLocaleDateString('es-PY');
    const cobertura = reporteComp.totalRodeo > 0
      ? Math.round((reporteComp.totalPresentes / reporteComp.totalRodeo) * 100) : 0;
    const todos = [...reporteComp.presentes, ...reporteComp.ausentes]
      .sort((a, b) => a.caravana.localeCompare(b.caravana));

    // Cabecera estancia
    doc.setFontSize(13);
    doc.setTextColor(46, 125, 50);
    doc.setFont(undefined, 'bold');
    doc.text('Estancia Medalla Milagrosa', 105, 12, { align: 'center' });
    doc.setFont(undefined, 'normal');
    doc.setDrawColor(46, 125, 50);
    doc.setLineWidth(0.5);
    doc.line(14, 15, 196, 15);

    // Encabezado
    doc.setFontSize(14);
    doc.setTextColor(46, 125, 50);
    doc.text('Reporte de Asistencia', 14, 23);
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Fecha: ${fecha}   Tipo: ${TIPO_LABEL[asistenciaActual.tipo]}   Estado: ${asistenciaActual.estado}`, 14, 31);
    if (asistenciaActual.observacion) {
      doc.text(`Observación: ${asistenciaActual.observacion}`, 14, 37);
    }

    // Resumen de stats
    const statsY = asistenciaActual.observacion ? 45 : 39;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Rodeo: ${reporteComp.totalRodeo}   Presentes: ${reporteComp.totalPresentes}   Ausentes: ${reporteComp.totalAusentes}   Cobertura: ${cobertura}%`, 14, statsY);

    // Tabla
    autoTable(doc, {
      startY: statsY + 6,
      head: [['Estado', 'Caravana', 'Color Car.', 'Clasificación', 'Nombre', 'Sexo', 'Raza']],
      body: todos.map(a => [
        a.presente ? 'Presente' : 'Ausente',
        a.caravana || '',
        a.colorCaravana || '-',
        a.clasificacion ? (CLASIF_LABEL[a.clasificacion] || a.clasificacion) : '-',
        a.nombre || '-',
        a.sexo === 'MACHO' ? 'Macho' : a.sexo === 'HEMBRA' ? 'Hembra' : '-',
        a.raza || '-',
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [46, 125, 50], textColor: 255, fontStyle: 'bold' },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          data.cell.styles.textColor = data.cell.raw === 'Presente' ? [39, 174, 96] : [231, 76, 60];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    const fechaArchivo = asistenciaActual.fecha;
    doc.save(`asistencia_${fechaArchivo}.pdf`);
  };

  const exportarExcel = () => {
    if (!asistenciaActual || !reporteComp) return;
    const todos = [...reporteComp.presentes, ...reporteComp.ausentes]
      .sort((a, b) => a.caravana.localeCompare(b.caravana));

    const filas = todos.map(a => ({
      'Estado':        a.presente ? 'Presente' : 'Ausente',
      'Caravana':      a.caravana || '',
      'Color Caravana':a.colorCaravana || '',
      'Clasificación': a.clasificacion ? (CLASIF_LABEL[a.clasificacion] || a.clasificacion) : '',
      'Nombre':        a.nombre || '',
      'Sexo':          a.sexo || '',
      'Raza':          a.raza || '',
    }));

    // Hoja resumen
    const resumen = [
      { Concepto: 'Fecha',     Valor: new Date(asistenciaActual.fecha + 'T00:00:00').toLocaleDateString('es-PY') },
      { Concepto: 'Tipo',      Valor: TIPO_LABEL[asistenciaActual.tipo] },
      { Concepto: 'Estado',    Valor: asistenciaActual.estado },
      { Concepto: 'Observación',Valor: asistenciaActual.observacion || '' },
      { Concepto: 'Total Rodeo',Valor: reporteComp.totalRodeo },
      { Concepto: 'Presentes', Valor: reporteComp.totalPresentes },
      { Concepto: 'Ausentes',  Valor: reporteComp.totalAusentes },
      { Concepto: 'Cobertura', Valor: `${reporteComp.totalRodeo > 0 ? Math.round((reporteComp.totalPresentes / reporteComp.totalRodeo) * 100) : 0}%` },
    ];

    const wb = XLSX.utils.book_new();
    const wsResumen = XLSX.utils.json_to_sheet(resumen);
    wsResumen['!cols'] = [{wch:16},{wch:20}];
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

    const wsDetalle = XLSX.utils.json_to_sheet(filas);
    wsDetalle['!cols'] = [{wch:10},{wch:12},{wch:14},{wch:16},{wch:16},{wch:8},{wch:12}];
    XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle');

    XLSX.writeFile(wb, `asistencia_${asistenciaActual.fecha}.xlsx`);
  };

  // ——— MODAL: REPORTE FINAL (disponible desde cualquier vista) ———
  if (mostrarReporte && asistenciaActual && reporteComp) {
    const todosReporte = [
      ...reporteComp.presentes,
      ...reporteComp.ausentes,
    ].sort((a, b) => a.caravana.localeCompare(b.caravana));
    const filasReporte = filtroReporte === 'presentes' ? reporteComp.presentes
      : filtroReporte === 'ausentes' ? reporteComp.ausentes
      : todosReporte;
    const cobertura = reporteComp.totalRodeo > 0
      ? Math.round((reporteComp.totalPresentes / reporteComp.totalRodeo) * 100) : 0;

    return (
      <div className="modal-overlay reporte-overlay">
        <div className="modal reporte-modal">
          <div className="reporte-header no-print-hidden">
            <h2>📋 Reporte de Asistencia</h2>
            <div className="reporte-meta">
              <span><strong>Fecha:</strong> {new Date(asistenciaActual.fecha + 'T00:00:00').toLocaleDateString('es-PY')}</span>
              <span><strong>Tipo:</strong> {TIPO_LABEL[asistenciaActual.tipo]}</span>
              <span><strong>Estado:</strong> {asistenciaActual.estado}</span>
              {asistenciaActual.observacion && <span><strong>Obs.:</strong> {asistenciaActual.observacion}</span>}
            </div>
          </div>

          {/* Estadísticas */}
          <div className="reporte-stats no-print-hidden">
            <div className="reporte-stat-card" style={{borderColor:'#555'}}>
              <span className="reporte-stat-num">{reporteComp.totalRodeo}</span>
              <span className="reporte-stat-label">🐄 Total Rodeo</span>
            </div>
            <div className="reporte-stat-card" style={{borderColor:'#27ae60'}}>
              <span className="reporte-stat-num" style={{color:'#27ae60'}}>{reporteComp.totalPresentes}</span>
              <span className="reporte-stat-label">✅ Presentes</span>
            </div>
            <div className="reporte-stat-card" style={{borderColor:'#e74c3c'}}>
              <span className="reporte-stat-num" style={{color:'#e74c3c'}}>{reporteComp.totalAusentes}</span>
              <span className="reporte-stat-label">❌ Ausentes</span>
            </div>
            <div className="reporte-stat-card" style={{borderColor:'#1a7ab5'}}>
              <span className="reporte-stat-num" style={{color:'#1a7ab5'}}>{cobertura}%</span>
              <span className="reporte-stat-label">📈 Cobertura</span>
            </div>
          </div>

          {/* Filtros */}
          <div className="reporte-filtros no-print">
            <button className={`btn-filtro ${filtroReporte === 'todos' ? 'active' : ''}`}
                    onClick={() => setFiltroReporte('todos')}>
              Todos ({reporteComp.totalRodeo})
            </button>
            <button className={`btn-filtro btn-filtro-presente ${filtroReporte === 'presentes' ? 'active' : ''}`}
                    onClick={() => setFiltroReporte('presentes')}>
              ✅ Presentes ({reporteComp.totalPresentes})
            </button>
            <button className={`btn-filtro btn-filtro-ausente ${filtroReporte === 'ausentes' ? 'active' : ''}`}
                    onClick={() => setFiltroReporte('ausentes')}>
              ❌ Ausentes ({reporteComp.totalAusentes})
            </button>
          </div>

          <div className="reporte-body">
            <table className="reporte-tabla">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Caravana</th>
                  <th>Color Car.</th>
                  <th>Clasificación</th>
                  <th>Nombre</th>
                  <th>Sexo</th>
                  <th>Raza</th>
                </tr>
              </thead>
              <tbody>
                {filasReporte.length === 0 ? (
                  <tr><td colSpan="7" style={{textAlign:'center',color:'#999',padding:'20px'}}>Sin datos</td></tr>
                ) : filasReporte.map(a => (
                  <tr key={a.id} className={a.presente ? 'fila-presente' : 'fila-ausente'}>
                    <td>
                      {a.presente
                        ? <span className="badge-comp presente">✅ Presente</span>
                        : <span className="badge-comp ausente">❌ Ausente</span>}
                    </td>
                    <td><strong>{a.caravana}</strong></td>
                    <td>
                      <span className="color-tag" style={{background: colorBg(a.colorCaravana)}}>
                        {a.colorCaravana || '-'}
                      </span>
                    </td>
                    <td>{a.clasificacion ? CLASIF_LABEL[a.clasificacion] || a.clasificacion : '-'}</td>
                    <td>{a.nombre || '-'}</td>
                    <td>{a.sexo === 'MACHO' ? '♂ Macho' : a.sexo === 'HEMBRA' ? '♀ Hembra' : '-'}</td>
                    <td>{a.raza || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="reporte-actions no-print">
            <button className="btn-secondary" onClick={() => setMostrarReporte(false)}>✕ Cerrar</button>
            <button className="btn-success" onClick={exportarExcel}>📥 Exportar Excel</button>
            <button className="btn-primary" onClick={exportarPDF}>📄 Exportar PDF</button>
          </div>
        </div>
      </div>
    );
  }

  // ——— VISTA: LISTA DE ASISTENCIAS ———
  if (vista === 'lista') return (
    <div className="ctrl-asistencia">
      <div className="ctrl-toolbar">
        <h2>📋 Control de Asistencia</h2>
        <div style={{display:'flex', gap:'8px'}}>
          <button className="btn-ayuda" onClick={() => setMostrarAyuda(true)}>❓ Ayuda</button>
          <button className="btn-primary" onClick={() => setMostrarFormNueva(true)}>+ Nueva Asistencia</button>
        </div>
      </div>

      {mostrarAyuda && (
        <AyudaModal titulo="Ayuda — Asistencia" pasos={AYUDA_ASISTENCIA} onClose={() => setMostrarAyuda(false)} />
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {exito && <div className="alert alert-success">{exito}</div>}

      {/* Formulario nueva asistencia */}
      {mostrarFormNueva && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMostrarFormNueva(false)}>
          <div className="modal modal-sm">
            <h3>Nueva Asistencia</h3>
            <form onSubmit={crearAsistencia} className="ficha-form">
              <div className="form-group">
                <label>Fecha *</label>
                <input type="date" value={formNueva.fecha}
                       onChange={e => setFormNueva(p => ({ ...p, fecha: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Tipo *</label>
                <select value={formNueva.tipo}
                        onChange={e => setFormNueva(p => ({ ...p, tipo: e.target.value }))}>
                  <option value="VACUNACION">Vacunación</option>
                  <option value="ANTIAFTOSA">Antiaftosa</option>
                  <option value="CONTROL">Control</option>
                  <option value="INSEMINACION">Inseminación</option>
                  <option value="OTROS">Otros</option>
                </select>
              </div>
              <div className="form-group">
                <label>Observación</label>
                <textarea rows={3} value={formNueva.observacion}
                          onChange={e => setFormNueva(p => ({ ...p, observacion: e.target.value }))}
                          placeholder="Detalles del conteo..." />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarFormNueva(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Crear y Comenzar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tabla de asistencias */}
      <div className="card">
        {loading ? <div className="loading">Cargando...</div> : (
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Observación</th>
                <th>Presentes</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {asistencias.length === 0 ? (
                <tr><td colSpan="6" style={{textAlign:'center',color:'#999',padding:'30px'}}>
                  No hay asistencias registradas
                </td></tr>
              ) : asistencias.map(a => (
                <tr key={a.id}>
                  <td>{new Date(a.fecha + 'T00:00:00').toLocaleDateString('es-PY')}</td>
                  <td><span className={`badge-tipo badge-tipo-${a.tipo?.toLowerCase()}`}>{TIPO_LABEL[a.tipo]}</span></td>
                  <td>{a.observacion || '-'}</td>
                  <td><strong>{a.totalPresentes}</strong> animales</td>
                  <td>
                    <span className={`badge badge-${a.estado?.toLowerCase()}`}>{a.estado}</span>
                  </td>
                  <td style={{display:'flex', gap:'6px'}}>
                    <button className="btn-primary btn-sm" onClick={() => abrirConteo(a.id)}>
                      {a.estado === 'ABIERTA' ? '✏️ Continuar' : '👁 Ver'}
                    </button>
                    <button className="btn-reporte btn-sm" onClick={() => verReporte(a.id)}>
                      🖨️ Reporte
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  // ——— VISTA: CONTEO ———
  if (vista === 'conteo' && asistenciaActual) return (
    <div className="ctrl-asistencia">
      <div className="ctrl-toolbar">
        <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
          <button className="btn-secondary btn-sm" onClick={() => { setVista('lista'); cargarLista(); }}>← Volver</button>
          <div>
            <h2 style={{margin:0}}>
              {TIPO_LABEL[asistenciaActual.tipo]} —&nbsp;
              {new Date(asistenciaActual.fecha + 'T00:00:00').toLocaleDateString('es-PY')}
            </h2>
            {asistenciaActual.observacion && <small style={{color:'#666'}}>{asistenciaActual.observacion}</small>}
          </div>
          <span className={`badge badge-${asistenciaActual.estado?.toLowerCase()}`}>{asistenciaActual.estado}</span>
        </div>
        <div style={{display:'flex', gap:'8px'}}>
          <button className="btn-reporte btn-sm" onClick={() => setMostrarReporte(true)}>
            🖨️ Reporte Final
          </button>
          <button className="btn-info btn-sm" onClick={verComparacion} disabled={loadingComp}>
            {loadingComp ? 'Cargando...' : '📊 Comparar con Rodeo'}
          </button>
          {asistenciaActual.estado === 'CERRADA' && (
            <button className="btn-vacuna btn-sm" onClick={() => setMostrarVacuna(true)}>
              💉 Vacunar Rodeo
            </button>
          )}
          {asistenciaActual.estado === 'CERRADA' && (
            <button className="btn-insem btn-sm" onClick={() => setMostrarInsem(true)}>
              🐄 Inseminación
            </button>
          )}
          {asistenciaActual.estado === 'ABIERTA'
            ? <button className="btn-danger btn-sm" onClick={cerrarAsistencia}>🔒 Cerrar Asistencia</button>
            : <button className="btn-secondary btn-sm" onClick={reabrirAsistencia}>🔓 Reabrir</button>
          }
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {exito && <div className="alert alert-success">{exito}</div>}

      {/* Modal vacunación masiva */}
      {mostrarVacuna && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMostrarVacuna(false)}>
          <div className="modal modal-md">
            <h3>💉 Vacunar Rodeo</h3>
            <p style={{color:'#666', marginBottom:'12px', fontSize:'0.9rem'}}>
              Se registrará la vacuna en los <strong>{asistenciaActual.totalPresentes}</strong> animales presentes en esta asistencia.
            </p>
            <form onSubmit={vacunarMasivo} className="ficha-form">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Vacuna *</label>
                  <input value={formVacuna.nombreVacuna} required
                    onChange={e => setFormVacuna(p => ({ ...p, nombreVacuna: e.target.value.toUpperCase() }))}
                    placeholder="Ej: AFTOSA, BRUCELOSIS" style={{textTransform:'uppercase'}} />
                </div>
                <div className="form-group">
                  <label>Laboratorio</label>
                  <input value={formVacuna.laboratorio}
                    onChange={e => setFormVacuna(p => ({ ...p, laboratorio: e.target.value.toUpperCase() }))}
                    placeholder="Ej: BIOGÉNESIS" style={{textTransform:'uppercase'}} />
                </div>
                <div className="form-group">
                  <label>Fecha Aplicación *</label>
                  <input type="date" value={formVacuna.fechaAplicacion} required
                    onChange={e => setFormVacuna(p => ({ ...p, fechaAplicacion: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Próxima Aplicación</label>
                  <input type="date" value={formVacuna.fechaProximaAplicacion}
                    onChange={e => setFormVacuna(p => ({ ...p, fechaProximaAplicacion: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Dosis</label>
                  <input value={formVacuna.dosis}
                    onChange={e => setFormVacuna(p => ({ ...p, dosis: e.target.value }))}
                    placeholder="Ej: 2ml" />
                </div>
                <div className="form-group">
                  <label>Lote</label>
                  <input value={formVacuna.lote}
                    onChange={e => setFormVacuna(p => ({ ...p, lote: e.target.value.toUpperCase() }))}
                    placeholder="Nro de lote" style={{textTransform:'uppercase'}} />
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Veterinario</label>
                  <input value={formVacuna.veterinario}
                    onChange={e => setFormVacuna(p => ({ ...p, veterinario: e.target.value.toUpperCase() }))}
                    placeholder="Nombre del veterinario" style={{textTransform:'uppercase'}} />
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Observaciones</label>
                  <textarea rows={2} value={formVacuna.observaciones}
                    onChange={e => setFormVacuna(p => ({ ...p, observaciones: e.target.value }))}
                    placeholder="Notas adicionales..." />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarVacuna(false)}>Cancelar</button>
                <button type="submit" className="btn-vacuna" disabled={guardandoVacuna}>
                  {guardandoVacuna ? 'Registrando...' : `💉 Registrar en ${asistenciaActual.totalPresentes} animales`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal inseminación masiva */}
      {mostrarInsem && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMostrarInsem(false)}>
          <div className="modal modal-md">
            <h3>🐄 Inseminación</h3>
            <p style={{color:'#666', marginBottom:'12px', fontSize:'0.9rem'}}>
              Se registrará la inseminación en los <strong>{asistenciaActual.totalPresentes}</strong> animales presentes en esta asistencia.
            </p>
            <form onSubmit={inseminarMasivo} className="ficha-form">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Fecha *</label>
                  <input type="date" value={formInsem.fecha} required
                    onChange={e => setFormInsem(p => ({ ...p, fecha: e.target.value }))} />
                </div>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Observaciones</label>
                  <textarea rows={2} value={formInsem.observaciones}
                    onChange={e => setFormInsem(p => ({ ...p, observaciones: e.target.value.toUpperCase() }))}
                    placeholder="Notas de la inseminación..." style={{textTransform:'uppercase'}} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarInsem(false)}>Cancelar</button>
                <button type="submit" className="btn-insem" disabled={guardandoInsem}>
                  {guardandoInsem ? 'Registrando...' : `🐄 Registrar en ${asistenciaActual.totalPresentes} animales`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Buscador de caravana */}
      {asistenciaActual.estado === 'ABIERTA' && (
        <div className="card buscar-card">
          <form onSubmit={buscarCaravana} className="buscar-form">
            <label>Ingresar número de caravana:</label>
            <div className="buscar-row">
              <input
                ref={inputRef}
                value={caravanaBuscar}
                onChange={e => { setCaravanaBuscar(e.target.value); setResultadosBusqueda([]); }}
                placeholder="Ej: 0001"
                autoFocus
                disabled={buscando}
              />
              <button type="submit" className="btn-primary" disabled={buscando || !caravanaBuscar.trim()}>
                {buscando ? 'Buscando...' : '🔍 Buscar'}
              </button>
            </div>
          </form>

          {/* Resultados de búsqueda cuando hay múltiples */}
          {resultadosBusqueda.length > 1 && (
            <div className="resultados-busqueda">
              <p style={{margin:'8px 0 6px', fontWeight:600, color:'#555'}}>
                Se encontraron {resultadosBusqueda.length} animales — elegí cuál agregar:
              </p>
              {resultadosBusqueda.map(a => (
                <div key={a.id} className="resultado-item" onClick={() => agregarPorId(a.id, a.caravana)}>
                  <span className="resultado-id">ID: {a.id}</span>
                  <strong>{a.caravana}</strong>
                  <span className="color-tag" style={{background: colorBg(a.colorCaravana)}}>
                    {a.colorCaravana || '-'}
                  </span>
                  <span>{a.clasificacion ? CLASIF_LABEL[a.clasificacion] || a.clasificacion : '-'}</span>
                  <span style={{color:'#666'}}>{a.nombre || ''}</span>
                  <span style={{color:'#999', fontSize:'0.82rem'}}>{a.sexo === 'MACHO' ? '♂' : a.sexo === 'HEMBRA' ? '♀' : ''}</span>
                  <span className="btn-agregar-resultado">+ Agregar</span>
                </div>
              ))}
            </div>
          )}

          <div className="conteo-total">
            <span>Total presentes: <strong>{asistenciaActual.totalPresentes}</strong></span>
          </div>
        </div>
      )}

      {asistenciaActual.estado === 'CERRADA' && (
        <div className="card" style={{padding:'12px 20px'}}>
          <strong>Total presentes: {asistenciaActual.totalPresentes} animales</strong>
        </div>
      )}

      {/* Grilla de animales agregados */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Caravana</th>
              <th>Color Car.</th>
              <th>Clasificación</th>
              <th>Nombre</th>
              <th>Sexo</th>
              <th>Raza</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(!asistenciaActual.detalles || asistenciaActual.detalles.length === 0) ? (
              <tr><td colSpan="8" style={{textAlign:'center',color:'#999',padding:'24px'}}>
                Aún no se agregaron animales
              </td></tr>
            ) : asistenciaActual.detalles.map((d, i) => (
              <tr key={d.id}>
                <td style={{color:'#999'}}>{i + 1}</td>
                <td><strong>{d.caravana}</strong></td>
                <td>
                  <span className="color-tag" style={{background: colorBg(d.colorCaravana)}}>
                    {d.colorCaravana || '-'}
                  </span>
                </td>
                <td>{d.clasificacion ? CLASIF_LABEL[d.clasificacion] || d.clasificacion : '-'}</td>
                <td>{d.nombre || '-'}</td>
                <td>{d.sexo === 'MACHO' ? '♂ Macho' : d.sexo === 'HEMBRA' ? '♀ Hembra' : '-'}</td>
                <td>{d.raza || '-'}</td>
                <td style={{display:'flex', gap:'6px'}}>
                  {onVerFicha && (
                    <button className="btn-info btn-sm" onClick={() => onVerFicha({ id: d.animalId })}>
                      📋 Ficha
                    </button>
                  )}
                  {asistenciaActual.estado === 'ABIERTA' && (
                    <button className="btn-danger btn-sm" onClick={() => quitarAnimal(d.animalId, d.caravana)}>✕</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ——— VISTA: COMPARACIÓN ———
  if (vista === 'comparacion' && comparacion) return (
    <div className="ctrl-asistencia">
      <div className="ctrl-toolbar">
        <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
          <button className="btn-secondary btn-sm" onClick={() => setVista('conteo')}>← Volver al Conteo</button>
          <h2 style={{margin:0}}>📊 Comparación contra Rodeo Activo</h2>
        </div>
      </div>

      {/* Resumen */}
      <div className="stats-row">
        <div className="stat-card stat-total">
          <span className="stat-num">{comparacion.totalRodeo}</span>
          <span className="stat-label">🐄 Total Rodeo</span>
        </div>
        <div className="stat-card stat-presente">
          <span className="stat-num">{comparacion.totalPresentes}</span>
          <span className="stat-label">✅ Presentes</span>
        </div>
        <div className="stat-card stat-ausente">
          <span className="stat-num">{comparacion.totalAusentes}</span>
          <span className="stat-label">❌ Ausentes</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">
            {comparacion.totalRodeo > 0
              ? Math.round((comparacion.totalPresentes / comparacion.totalRodeo) * 100)
              : 0}%
          </span>
          <span className="stat-label">📈 Cobertura</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="card" style={{padding:'12px 20px'}}>
        <div className="filtros">
          <button className={`btn-filtro ${filtroComp === 'todos' ? 'active' : ''}`}
                  onClick={() => setFiltroComp('todos')}>
            Todos ({comparacion.totalRodeo})
          </button>
          <button className={`btn-filtro btn-filtro-presente ${filtroComp === 'presentes' ? 'active' : ''}`}
                  onClick={() => setFiltroComp('presentes')}>
            ✅ Presentes ({comparacion.totalPresentes})
          </button>
          <button className={`btn-filtro btn-filtro-ausente ${filtroComp === 'ausentes' ? 'active' : ''}`}
                  onClick={() => setFiltroComp('ausentes')}>
            ❌ Ausentes ({comparacion.totalAusentes})
          </button>
        </div>
      </div>

      {/* Tabla comparación */}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Estado</th>
              <th>Caravana</th>
              <th>Color Car.</th>
              <th>Clasificación</th>
              <th>Nombre</th>
              <th>Sexo</th>
              <th>Raza</th>
            </tr>
          </thead>
          <tbody>
            {animalesComparacion().map(a => (
              <tr key={a.id} className={a.presente ? 'fila-presente' : 'fila-ausente'}>
                <td>
                  {a.presente
                    ? <span className="badge-comp presente">✅ Presente</span>
                    : <span className="badge-comp ausente">❌ Ausente</span>
                  }
                </td>
                <td><strong>{a.caravana}</strong></td>
                <td>
                  <span className="color-tag" style={{background: colorBg(a.colorCaravana)}}>
                    {a.colorCaravana || '-'}
                  </span>
                </td>
                <td>{a.clasificacion ? CLASIF_LABEL[a.clasificacion] || a.clasificacion : '-'}</td>
                <td>{a.nombre || '-'}</td>
                <td>{a.sexo === 'MACHO' ? '♂ Macho' : a.sexo === 'HEMBRA' ? '♀ Hembra' : '-'}</td>
                <td>{a.raza || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return null;
}
